import React from "react";
import { descreverDocumentoEstrangeiro } from "../utils/documentoEstrangeiro";

// ── Helpers ───────────────────────────────────────────────────────────────────

const LABEL_CAMPO = {
  Locadora: "Locadora",
  Locataria: "Locatária",
  Fiador: "Fiador",
  Seguradora: "Seguradora",
};

const ESTADOS_CIVIS_SEM_CONJUGE = ["SOLTEIRO(A)", "DIVORCIADO(A)", "VIÚVO(A)", "SEPARADO(A) JUDICIALMENTE"];

function v(val) {
  return val === null || val === undefined || val === "" ? "—" : val;
}

function formatData(d) {
  if (!d) return "—";

  const s = String(d).substring(0, 10);
  const [ano, mes, dia] = s.split("-");

  if (!ano || !mes || !dia) return s;

  return `${dia}.${mes}.${ano}`;
}

function formatMoeda(x) {
  const n = parseFloat(x);

  return isNaN(n)
    ? "—"
    : n.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
}

function enderecoCompleto(f, prefixo) {
  const campo = (nome) => (prefixo ? f[`${prefixo}${nome}`] : f[nome.charAt(0).toLowerCase() + nome.slice(1)]);

  const linha1 = [campo("Endereco"), campo("Numero")].filter(Boolean).join(", ");

  const partes = [linha1, campo("Complemento"), campo("Bairro"), [campo("Cidade"), campo("Uf")].filter(Boolean).join("/"), campo("Cep")].filter(Boolean);

  return partes.length ? partes.join(" — ") : "—";
}

// ── Estilos ───────────────────────────────────────────────────────────────────

const pageStyle = {
  fontFamily: "Arial, Helvetica, sans-serif",
  fontSize: "9pt",
  lineHeight: "1.2",
  color: "#000",
  backgroundColor: "#fff",
  padding: "8mm 15mm 12mm 15mm",
  maxWidth: "210mm",
  margin: "0 auto",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: "3mm",
  borderBottom: "1px solid #c00",
  paddingBottom: "2mm",
};

const logoStyle = {
  fontSize: "18pt",
  fontWeight: "bold",
  letterSpacing: "-1px",
};

const titleStyle = {
  textAlign: "center",
  fontSize: "11pt",
  fontWeight: "bold",
  letterSpacing: "2px",
  marginBottom: "6mm",
};

const sectionTitleStyle = {
  textAlign: "center",
  fontWeight: "bold",
  fontSize: "9pt",
  letterSpacing: "1px",
  backgroundColor: "#d9d9d9",
  color: "#000",
  border: "1px solid #ccc",
  padding: "3px 8px",
  marginBottom: 0,
  WebkitPrintColorAdjust: "exact",
  printColorAdjust: "exact",
};

const fichaTitleStyle = {
  fontWeight: "bold",
  fontSize: "10pt",
  marginTop: "6mm",
  marginBottom: "2mm",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  marginBottom: "4mm",
  tableLayout: "fixed",
};

const tdLabelBase = {
  width: "42%",
  fontStyle: "italic",
  padding: "2px 8px",
  border: "1px solid #ccc",
  verticalAlign: "top",
  fontSize: "8pt",
  lineHeight: "1.2",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  WebkitPrintColorAdjust: "exact",
  printColorAdjust: "exact",
};

const tdValueBase = {
  width: "58%",
  padding: "2px 8px",
  border: "1px solid #ccc",
  verticalAlign: "top",
  fontSize: "8pt",
  lineHeight: "1.2",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  WebkitPrintColorAdjust: "exact",
  printColorAdjust: "exact",
};

// ── Componente ────────────────────────────────────────────────────────────────

export default function FichaCadastroPrintTemplate({ proposta, fichas, modoTemplate = false }) {
  if (!proposta) return null;

  const lista = fichas ?? [];
  let _rowIdx = 0;

  // ==========================================================================
  // TABELA NORMAL
  // ==========================================================================

  const Table = ({ titulo, rows, className = "" }) => {
    _rowIdx = 0;

    return (
      <div className={className}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th colSpan={2} style={sectionTitleStyle}>
                {titulo}
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.map(({ label, value }) => {
              const bg = _rowIdx++ % 2 === 0 ? "#ffffff" : "#f3e8e6";

              return (
                <tr key={label}>
                  <td style={{ ...tdLabelBase, backgroundColor: bg }}>{label}</td>

                  <td style={{ ...tdValueBase, backgroundColor: bg }}>{v(value)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ==========================================================================
  // TABELA TEMPLATE
  // ==========================================================================

  const TableTemplate = ({ titulo, campos, className = "" }) => {
    _rowIdx = 0;

    return (
      <div className={`template-section ${className}`}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th colSpan={2} style={sectionTitleStyle}>
                {titulo}
              </th>
            </tr>
          </thead>

          <tbody>
            {campos.map((label) => {
              const bg = _rowIdx++ % 2 === 0 ? "#ffffff" : "#f3e8e6";

              return (
                <tr key={label}>
                  <td style={{ ...tdLabelBase, backgroundColor: bg }}>{label}</td>
                  <td
                    style={{
                      ...tdValueBase,
                      backgroundColor: bg,
                      height: "18px",
                    }}
                  >
                    &nbsp;
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ==========================================================================
  // IMPRESSÃO NORMAL — PESSOA JURÍDICA
  // ==========================================================================

  const renderPJ = (f) => (
    <>
      <Table
        titulo="Dados Cadastrais"
        rows={[
          { label: "CNPJ", value: f.cnpj },
          ...(f.estrangeiro && f.documentoEstrangeiroNumero ? [{ label: "Documento Estrangeiro", value: descreverDocumentoEstrangeiro(f) }] : []),
          { label: "Razão Social", value: f.razaoSocial },
        ]}
      />

      <Table
        titulo="Endereço"
        rows={[
          { label: "Endereço", value: enderecoCompleto(f, "") },
          { label: "Telefone Comercial", value: f.telefone },
          { label: "Celular", value: f.celular },
          { label: "E-mail", value: f.email },
        ]}
      />

      <Table
        titulo="Dados Empresariais"
        rows={[
          {
            label: "Data de Constituição",
            value: formatData(f.dataConstituicao),
          },
          {
            label: "Capital Social",
            value: f.capitalSocial ? formatMoeda(f.capitalSocial) : "—",
          },
        ]}
      />

      {(f.enderecoFilial || f.telefoneFilial) && (
        <Table
          titulo="Filial"
          rows={[
            {
              label: "Endereço da Filial",
              value: enderecoCompleto(f, "Filial"),
            },
            { label: "Telefone da Filial", value: f.telefoneFilial },
            { label: "Celular da Filial", value: f.celularFilial },
          ]}
        />
      )}

      {f.pfNome && (
        <Table
          titulo="Representante Legal"
          rows={[
            { label: "Nome", value: f.pfNome },
            { label: "CPF", value: f.cpf },
            { label: "RG", value: f.rg },
            { label: "Órgão Emissor", value: f.rgOrgao },
            { label: "Data de Emissão", value: formatData(f.rgEmissao) },
            {
              label: "Data de Nascimento",
              value: formatData(f.pfDtNascimento),
            },
            { label: "Nacionalidade", value: f.pfNacionalidade },
            { label: "Naturalidade", value: f.pfNaturalidade },
            { label: "Estado Civil", value: f.pfEstadoCivil },
            ...(f.pfEstadoCivil === "CASADO(A)"
              ? [
                  {
                    label: "Regime de Casamento",
                    value: f.pfRegimeCasamento,
                  },
                ]
              : []),
            { label: "Profissão", value: f.pfProfissao },
            { label: "Endereço", value: enderecoCompleto(f, "pf") },
            { label: "Telefone", value: f.pfTelefone },
            { label: "Celular", value: f.pfCelular },
            { label: "E-mail", value: f.pfEmail },
          ]}
        />
      )}

      {f.pfNome && f.pfEstadoCivil && !ESTADOS_CIVIS_SEM_CONJUGE.includes(f.pfEstadoCivil) && (
        <Table
          titulo="Cônjuge do Representante"
          rows={[
            { label: "Nome do Cônjuge", value: f.pfConjuge },
            {
              label: "Data de Nascimento",
              value: formatData(f.pfConjugeDtNascimento),
            },
            { label: "Nacionalidade", value: f.pfConjugeNacionalidade },
            { label: "Naturalidade", value: f.pfConjugeNaturalidade },
            { label: "Profissão", value: f.pfConjugeProfissao },
            { label: "CPF do Cônjuge", value: f.conjugeCpf },
            { label: "RG do Cônjuge", value: f.conjugeRg },
            { label: "Órgão Emissor", value: f.conjugeRgOrgao },
            {
              label: "Data de Emissão",
              value: formatData(f.conjugeRgEmissao),
            },
            { label: "Telefone do Cônjuge", value: f.conjugeTelefone },
            { label: "Celular do Cônjuge", value: f.conjugeCelular },
            { label: "E-mail do Cônjuge", value: f.conjugeEmail },
          ]}
        />
      )}

      <Table
        titulo="Dados Operacionais"
        className="print-dados-operacionais"
        rows={[
          { label: "Ramo de Atividade", value: f.ramoAtividade },
          { label: "Produtos/Serviços", value: f.produtosServicos },
          { label: "Qtde. de Funcionários", value: f.qtdeFuncionarios },
          {
            label: "Faturamento Mensal",
            value: f.faturamentoMensal ? formatMoeda(f.faturamentoMensal) : "—",
          },
        ]}
      />
    </>
  );

  // ==========================================================================
  // IMPRESSÃO NORMAL — PESSOA FÍSICA
  // ==========================================================================

  const renderPF = (f) => {
    const temConjuge = f.pfEstadoCivil && !ESTADOS_CIVIS_SEM_CONJUGE.includes(f.pfEstadoCivil);

    return (
      <>
        <Table
          titulo="Dados Pessoais e Documentos"
          rows={[
            { label: "Nome", value: f.pfNome || f.razaoSocial },
            {
              label: "Data de Nascimento",
              value: formatData(f.pfDtNascimento),
            },
            { label: "Nacionalidade", value: f.pfNacionalidade },
            { label: "Naturalidade", value: f.pfNaturalidade },
            { label: "Estado Civil", value: f.pfEstadoCivil },
            ...(f.pfEstadoCivil === "CASADO(A)"
              ? [
                  {
                    label: "Regime de Casamento",
                    value: f.pfRegimeCasamento,
                  },
                ]
              : []),
            { label: "Profissão", value: f.pfProfissao },
            { label: "CPF", value: f.cpf },
            ...(f.estrangeiro && f.documentoEstrangeiroNumero ? [{ label: "Documento Estrangeiro", value: descreverDocumentoEstrangeiro(f) }] : []),
            { label: "RG", value: f.rg },
            { label: "Órgão Emissor", value: f.rgOrgao },
            { label: "Data de Emissão", value: formatData(f.rgEmissao) },
          ]}
        />

        <Table
          titulo="Endereço e Contato"
          rows={[
            { label: "Endereço", value: enderecoCompleto(f, "pf") },
            { label: "Telefone", value: f.pfTelefone },
            { label: "Celular", value: f.pfCelular },
            { label: "E-mail", value: f.pfEmail || f.email },
          ]}
        />

        {temConjuge && (
          <Table
            titulo="Cônjuge"
            rows={[
              { label: "Nome do Cônjuge", value: f.pfConjuge },
              {
                label: "Data de Nascimento",
                value: formatData(f.pfConjugeDtNascimento),
              },
              { label: "Nacionalidade", value: f.pfConjugeNacionalidade },
              { label: "Naturalidade", value: f.pfConjugeNaturalidade },
              { label: "Profissão", value: f.pfConjugeProfissao },
              { label: "CPF do Cônjuge", value: f.conjugeCpf },
              { label: "RG do Cônjuge", value: f.conjugeRg },
              { label: "Órgão Emissor", value: f.conjugeRgOrgao },
              {
                label: "Data de Emissão",
                value: formatData(f.conjugeRgEmissao),
              },
              { label: "Telefone do Cônjuge", value: f.conjugeTelefone },
              { label: "Celular do Cônjuge", value: f.conjugeCelular },
              { label: "E-mail do Cônjuge", value: f.conjugeEmail },
            ]}
          />
        )}

        {f.pfEmpresaTrabalha && (
          <Table
            titulo="Emprego"
            rows={[
              { label: "Empresa", value: f.pfEmpresaTrabalha },
              { label: "Cargo", value: f.pfEmpresaCargo },
              {
                label: "Data de Admissão",
                value: formatData(f.pfEmpresaDtAdmissao),
              },
              {
                label: "Salário",
                value: f.pfEmpresaSalario ? formatMoeda(f.pfEmpresaSalario) : "—",
              },
              {
                label: "Endereço da Empresa",
                value: enderecoCompleto(f, "pfEmpresa"),
              },
              ...(f.pfEmpresaOutroRendimento
                ? [
                    {
                      label: "Outro Rendimento",
                      value: f.pfEmpresaOutroRendimentoValor ? formatMoeda(f.pfEmpresaOutroRendimentoValor) : "—",
                    },
                    {
                      label: "Origem do Rendimento",
                      value: f.pfEmpresaOutroRendimentoProvem,
                    },
                  ]
                : []),
            ]}
          />
        )}

        {temConjuge && f.conjugePossuiRenda && f.conjugeEmpresaTrabalha && (
          <Table
            titulo="Emprego do Cônjuge"
            rows={[
              { label: "Empresa", value: f.conjugeEmpresaTrabalha },
              { label: "Cargo", value: f.conjugeEmpresaCargo },
              {
                label: "Data de Admissão",
                value: formatData(f.conjugeEmpresaDtAdmissao),
              },
              {
                label: "Salário",
                value: f.conjugeEmpresaSalario ? formatMoeda(f.conjugeEmpresaSalario) : "—",
              },
              {
                label: "Endereço da Empresa",
                value: enderecoCompleto(f, "conjugeEmpresa"),
              },
              ...(f.conjugeEmpresaOutroRendimento
                ? [
                    {
                      label: "Outro Rendimento",
                      value: f.conjugeEmpresaOutroRendimentoValor ? formatMoeda(f.conjugeEmpresaOutroRendimentoValor) : "—",
                    },
                    {
                      label: "Origem do Rendimento",
                      value: f.conjugeEmpresaOutroRendimentoProvem,
                    },
                  ]
                : []),
            ]}
          />
        )}
      </>
    );
  };

  // ==========================================================================
  // TEMPLATE FC — SEM DADOS
  // ==========================================================================

  if (modoTemplate) {
    return (
      <div id="print-fichas" style={pageStyle}>
        {/* eslint-disable-next-line react/no-danger */}
        <style
          dangerouslySetInnerHTML={{
            __html: `
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }

              #print-fichas table thead th {
                background-color: #d9d9d9 !important;
              }

              .template-section {
                break-inside: avoid;
                page-break-inside: avoid;
              }

              .template-representante {
                padding-top: 12mm;
              }

              .template-emprego-renda {
                padding-top: 12mm;
              }

              .template-checklist-conjuge {
                padding-top: 12mm;
              }

              .template-nova-pagina {
                page-break-before: always;
                break-before: page;
                padding-top: 8mm;
              }
            `,
          }}
        />

        <div style={headerStyle}>
          <div style={{ flex: 1 }} />
          <div style={{ flex: 1, textAlign: "right" }}>
            <span style={logoStyle}>
              VR<span style={{ color: "#c00" }}>i</span>
            </span>
          </div>
        </div>

        <div style={titleStyle}>TEMPLATE — FICHA DE CADASTRO</div>

        {/* PESSOA JURÍDICA */}

        <div style={fichaTitleStyle}>PESSOA JURÍDICA</div>

        <TableTemplate titulo="DADOS CADASTRAIS" campos={["CNPJ", "Razão Social", "Objeto Social Principal", "Outros Objetos Sociais"]} />

        <TableTemplate
          titulo="ENDEREÇO PRINCIPAL"
          campos={["CEP", "Logradouro", "Número", "Complemento", "Bairro", "Cidade", "UF", "Telefone Comercial", "Celular", "E-mail"]}
        />

        <TableTemplate
          titulo="DADOS EMPRESARIAIS"
          campos={["Data de Constituição", "Capital Social (R$)", "Faturamento Mensal (R$)", "Qtde. Funcionários", "Ramo de Atividade", "Produtos / Serviços"]}
        />

        <TableTemplate titulo="ENDEREÇO DA FILIAL" campos={["CEP", "Logradouro", "Número", "Complemento", "Bairro", "Cidade", "UF", "Telefone", "Celular"]} />

        <TableTemplate
          titulo="DADOS CADASTRAIS DO REPRESENTANTE"
          className="template-representante"
          campos={[
            "Nome Completo do Representante",
            "Data de Nascimento",
            "Estado Civil",
            "Regime de Casamento",
            "Nacionalidade",
            "Naturalidade",
            "Profissão",
            "CPF do Representante",
            "RG",
            "Órgão Emissor do RG",
            "Data de Emissão RG",
            "Telefone Comercial",
            "Celular",
            "E-mail",
          ]}
        />

        <TableTemplate
          titulo="CÔNJUGE DO REPRESENTANTE"
          campos={[
            "Nome do Cônjuge",
            "Data de Nascimento",
            "Profissão",
            "Nacionalidade",
            "Naturalidade",
            "CPF do Cônjuge",
            "RG do Cônjuge",
            "Órgão Emissor do RG",
            "Data de Emissão RG",
            "Telefone Comercial",
            "Celular",
            "E-mail",
          ]}
        />

        <TableTemplate
          titulo="CHECKLIST DE DOCUMENTOS — PJ"
          campos={[
            "Contrato Social com nº JUCESP e últimas alterações",
            "Procuração indicando diretores autorizados a assinar o contrato de aluguel",
            "Cartão do CNPJ",
            "Faturamento dos últimos 12 meses assinado pelos responsáveis",
            "Balanço ou balancete mais recente assinado pelo responsável",
            "RG e CPF ou CNH dos autorizados a assinar",
            "Comprovante de endereço dos autorizados a assinar",
            "Matrícula atualizada do imóvel à título de capacidade econômica",
            "Espelho do IPTU do imóvel à título de capacidade econômica",
            "Foto do Imóvel",
            "Comprovante de Patrimônio",
            "Link do Imóvel no Google Maps",
            "Observação Documental",
          ]}
        />

        <TableTemplate titulo="OBSERVAÇÃO" campos={["Observação"]} />

        {/* PESSOA FÍSICA */}

        <div className="template-nova-pagina">
          <div style={headerStyle}>
            <div style={{ flex: 1 }} />
            <div style={{ flex: 1, textAlign: "right" }}>
              <span style={logoStyle}>
                VR<span style={{ color: "#c00" }}>i</span>
              </span>
            </div>
          </div>

          <div style={titleStyle}>TEMPLATE — FICHA DE CADASTRO</div>
          <div style={fichaTitleStyle}>PESSOA FÍSICA</div>
        </div>

        <TableTemplate
          titulo="DADOS PESSOAIS E DOCUMENTOS"
          campos={[
            "Nome Completo",
            "Data de Nascimento",
            "Estado Civil",
            "Regime de Casamento",
            "Nacionalidade",
            "Naturalidade",
            "Profissão",
            "CPF",
            "RG",
            "Órgão Emissor do RG",
            "Data de Emissão RG",
          ]}
        />

        <TableTemplate
          titulo="ENDEREÇO E CONTATO"
          campos={["CEP", "Logradouro", "Número", "Complemento", "Bairro", "Cidade", "UF", "Telefone Residencial", "Celular", "E-mail"]}
        />

        <TableTemplate
          titulo="CÔNJUGE"
          campos={[
            "Nome do Cônjuge",
            "Data de Nascimento",
            "Profissão",
            "Nacionalidade",
            "Naturalidade",
            "CPF do Cônjuge",
            "RG do Cônjuge",
            "Órgão Emissor do RG",
            "Data de Emissão RG",
            "Telefone Comercial",
            "Celular",
            "E-mail",
          ]}
        />

        <TableTemplate
          titulo="EMPREGO / RENDA"
          className="template-emprego-renda"
          campos={[
            "Empresa onde Trabalha",
            "Data de Admissão",
            "Cargo / Função",
            "Salário (R$)",
            "CEP da Empresa",
            "Logradouro da Empresa",
            "Número",
            "Complemento",
            "Bairro",
            "Cidade",
            "UF",
            "Possui outro rendimento?",
            "Valor do outro rendimento (R$)",
            "Proveniente de",
          ]}
        />

        <TableTemplate
          titulo="EMPREGO / RENDA DO CÔNJUGE"
          campos={[
            "Cônjuge possui renda?",
            "Empresa onde Trabalha",
            "Data de Admissão",
            "Cargo / Função",
            "Salário (R$)",
            "CEP da Empresa",
            "Logradouro da Empresa",
            "Número",
            "Complemento",
            "Bairro",
            "Cidade",
            "UF",
            "Cônjuge possui outro rendimento?",
            "Valor do outro rendimento (R$)",
            "Proveniente de",
          ]}
        />

        <TableTemplate
          titulo="CHECKLIST DE DOCUMENTOS — PF"
          campos={[
            "RG e CPF ou CNH",
            "Comprovante de endereço",
            "3 últimos comprovantes mensais de renda líquida",
            "Certidão de nascimento, Casamento ou União Estável",
            "Declaração de IRPF com recibo de entrega",
            "Comprovante de vínculo empregatício",
            "Matrícula atualizada do imóvel à título de capacidade econômica",
            "Espelho do IPTU do imóvel à título de capacidade econômica",
            "Foto do Imóvel",
            "Comprovante de Patrimônio",
            "Link do Imóvel no Google Maps",
            "Observação Documental",
          ]}
        />

        <TableTemplate
          titulo="CHECKLIST DE DOCUMENTOS DO CÔNJUGE"
          className="template-checklist-conjuge"
          campos={["RG e CPF ou CNH do cônjuge", "3 últimos comprovantes mensais de renda líquida", "Declaração de IRPF com recibo de entrega"]}
        />

        <TableTemplate titulo="OBSERVAÇÃO" campos={["Observação"]} />
      </div>
    );
  }

  // ==========================================================================
  // IMPRESSÃO NORMAL DAS FICHAS EXISTENTES
  // ==========================================================================

  return (
    <div id="print-fichas" style={pageStyle}>
      {/* eslint-disable-next-line react/no-danger */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }

      #print-fichas table thead th {
        background-color: #d9d9d9 !important;
      }

      .print-dados-operacionais {
        padding-top: 12mm;
        break-inside: avoid;
        page-break-inside: avoid;
      }
    `,
        }}
      />

      <div style={headerStyle}>
        <div style={{ flex: 1 }} />
        <div style={{ flex: 1, textAlign: "right" }}>
          <span style={logoStyle}>
            VR<span style={{ color: "#c00" }}>i</span>
          </span>
        </div>
      </div>

      <div style={titleStyle}>{proposta.codigo ? `${proposta.codigo} — FICHAS DE CADASTRO` : "FICHAS DE CADASTRO"}</div>

      <div style={{ fontSize: "8pt", marginBottom: "4mm" }}>Imóvel: {v(proposta.enderecoImovel)}</div>

      {lista.length === 0 && <div style={{ fontStyle: "italic", color: "#666" }}>Nenhuma ficha encontrada para esta proposta.</div>}

      {lista.map((f) => (
        <div key={f.id}>
          <div style={fichaTitleStyle}>
            {LABEL_CAMPO[f.campoProposta] ?? f.campoProposta} — {f.tipoPessoa === "PJ" ? "Pessoa Jurídica" : "Pessoa Física"}
          </div>

          {f.tipoPessoa === "PJ" ? renderPJ(f) : renderPF(f)}
        </div>
      ))}
    </div>
  );
}
